import Sentry from "@/integrations/Sentry";
import {
  Document,
  ExternalHyperlink,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import { remark } from "remark";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";
import strip from "strip-markdown";
import TurndownService from "turndown";

// Define basic types for AST nodes
interface UnistNode {
  type: string;
  children?: UnistNode[];
  value?: string;
  depth?: number; // For headings
  ordered?: boolean; // For lists
  url?: string; // For links
  title?: string;
  alt?: string;
}

/** Trigger a file download from an in-memory blob (same-origin blob URL). */
export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const downloadAsFile = (
  content: string,
  filename: string,
  mimeType: string,
) => {
  const blob = new Blob([content], { type: mimeType });
  downloadBlob(blob, filename);
};

export const exportToPlainText = async (content: string, filename: string) => {
  try {
    const file = await remark().use(strip).process(content);
    const text = String(file);
    downloadAsFile(text, `${filename}.txt`, "text/plain");
  } catch (error) {
    Sentry.captureException(error);
    // Fallback
    downloadAsFile(content, `${filename}.txt`, "text/plain");
  }
};

export const exportToDocx = async (content: string, filename: string) => {
  try {
    const ast = remark().parse(content) as UnistNode;
    const docChildren: Paragraph[] = [];

    // Recursive helper to build TextRun options
    const buildTextRuns = (
      nodes: UnistNode[],
      currentStyle: {
        bold?: boolean;
        italics?: boolean;
        font?: string;
      } = { font: "Calibri" },
    ): (TextRun | ExternalHyperlink)[] => {
      const runs: (TextRun | ExternalHyperlink)[] = [];

      for (const node of nodes) {
        if (node.type === "text" && node.value) {
          runs.push(
            new TextRun({
              text: node.value,
              ...currentStyle,
            }),
          );
        } else if (node.type === "strong" && node.children) {
          runs.push(
            ...buildTextRuns(node.children, { ...currentStyle, bold: true }),
          );
        } else if (node.type === "emphasis" && node.children) {
          runs.push(
            ...buildTextRuns(node.children, { ...currentStyle, italics: true }),
          );
        } else if (node.type === "inlineCode" && node.value) {
          runs.push(
            new TextRun({
              text: node.value,
              font: "Courier New",
              ...currentStyle,
            }),
          );
        } else if (node.type === "link" && node.children && node.url) {
          // Create a proper hyperlink
          const linkText = node.children.map((c) => c.value || "").join("");
          runs.push(
            new ExternalHyperlink({
              children: [
                new TextRun({
                  text: linkText,
                  style: "Hyperlink",
                  ...currentStyle,
                }),
              ],
              link: node.url,
            }),
          );
        } else if (node.children) {
          runs.push(...buildTextRuns(node.children, currentStyle));
        }
      }
      return runs;
    };

    const processNode = (node: UnistNode, indentLevel = 0) => {
      if (node.type === "paragraph" && node.children) {
        docChildren.push(
          new Paragraph({
            children: buildTextRuns(node.children),
            spacing: { after: 200 },
            indent: { left: indentLevel * 720 },
          }),
        );
      } else if (node.type === "heading" && node.children && node.depth) {
        const levelMap: Record<number, any> = {
          1: HeadingLevel.HEADING_1,
          2: HeadingLevel.HEADING_2,
          3: HeadingLevel.HEADING_3,
          4: HeadingLevel.HEADING_4,
          5: HeadingLevel.HEADING_5,
          6: HeadingLevel.HEADING_6,
        };

        // Extract text content for headings with explicit black color
        const headingRuns = node.children.map((child) => {
          if (child.type === "text" && child.value) {
            return new TextRun({
              text: child.value,
              color: "000000", // Black color
              font: "Calibri",
            });
          }
          return new TextRun({ text: "", color: "000000" });
        });

        docChildren.push(
          new Paragraph({
            children: headingRuns,
            heading: levelMap[node.depth] || HeadingLevel.HEADING_1,
            spacing: { before: 240, after: 120 },
          }),
        );
      } else if (node.type === "list") {
        node.children?.forEach((listItem) => {
          processNode(listItem, indentLevel);
        });
      } else if (node.type === "listItem" && node.children) {
        node.children.forEach((child) => {
          if (child.type === "paragraph" && child.children) {
            // Manual bullet for now as it is robust
            docChildren.push(
              new Paragraph({
                children: [
                  new TextRun({ text: "• ", bold: true }),
                  ...buildTextRuns(child.children),
                ],
                indent: { left: (indentLevel + 1) * 720, hanging: 360 },
                spacing: { after: 100 },
              }),
            );
          } else {
            processNode(child, indentLevel + 1);
          }
        });
      } else if (node.type === "code" && node.value) {
        const lines = node.value.split("\n");
        lines.forEach((line) => {
          docChildren.push(
            new Paragraph({
              children: [
                new TextRun({ text: line, font: "Courier New", size: 20 }),
              ],
              indent: { left: indentLevel * 720 },
              spacing: { line: 240 }, // Single spacing roughly
            }),
          );
        });
      } else if (node.type === "blockquote" && node.children) {
        // Process children but increase indent
        node.children.forEach((child) => processNode(child, indentLevel + 1));
      } else if (node.children) {
        node.children.forEach((child) => processNode(child, indentLevel));
      }
    };

    if (ast.children) {
      ast.children.forEach((child) => processNode(child));
    }

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: docChildren,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    downloadBlob(blob, `${filename}.docx`);
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

// ---------------------------------------------------------------------------
// HTML artifact export utilities
// ---------------------------------------------------------------------------

/**
 * Print arbitrary HTML as PDF via a hidden iframe (opens the browser print dialog).
 */
export const exportPrintableHtmlAsPdf = (
  content: string,
  _filename: string,
): void => {
  const iframe = document.createElement("iframe");
  iframe.style.cssText =
    "position:fixed;top:0;left:0;width:100%;height:100%;" +
    "opacity:0;pointer-events:none;z-index:-1;border:0;";

  const previouslyFocused = document.activeElement as HTMLElement | null;

  const cleanup = () => {
    window.removeEventListener("message", onMessage);
    clearTimeout(fallback);
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    requestAnimationFrame(() => {
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus({ preventScroll: true });
      }
      window.focus();
    });
  };

  const onMessage = (e: MessageEvent) => {
    if (e.data === "__cohesive_afterprint__") cleanup();
  };
  window.addEventListener("message", onMessage);
  const fallback = setTimeout(cleanup, 60_000);

  const printSetup = `<script>
window.onload=function(){
  document.title='';
  var s=document.createElement('style');
  s.textContent='@page{margin:0}@media print{body{padding:.75cm!important;box-sizing:border-box!important}}';
  document.head.appendChild(s);
  window.print();
  parent.postMessage('__cohesive_afterprint__','*');
};
<\/script>`;

  let prepared = content;
  if (prepared.includes("</body>")) {
    prepared = prepared.replace("</body>", `${printSetup}\n</body>`);
  } else {
    prepared = prepared + "\n" + printSetup;
  }

  iframe.srcdoc = prepared;
  document.body.appendChild(iframe);
};

/**
 * Print the HTML content as PDF without opening a new tab.
 *
 * Uses a hidden full-size iframe with srcdoc. The iframe's own window.onload
 * clears the title, injects print styles, and calls window.print().
 * After print() returns (dialog dismissed), postMessage notifies the parent
 * which removes the iframe and restores focus via a rAF to let the browser
 * settle its own focus bookkeeping first.
 */
export const exportHtmlAsPdf = (content: string, filename: string): void => {
  exportPrintableHtmlAsPdf(content, filename);
};

function escapeHtmlForPrint(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Plain text → print dialog PDF (same iframe mechanism as HTML export). */
export const exportPlainTextAsPdf = (text: string, filename: string): void => {
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<style>
  body {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
    padding: .75cm;
    box-sizing: border-box;
  }
</style>
</head>
<body>${escapeHtmlForPrint(text)}</body>
</html>`;
  exportPrintableHtmlAsPdf(html, filename);
};

/**
 * Convert an HTML string to Markdown using Turndown.
 * Parses via DOMParser first so only <body> content is converted —
 * <head>, <style>, and <script> tags are never seen by Turndown.
 */
export const htmlToMarkdown = (html: string): string => {
  const td = new TurndownService({
    headingStyle: "atx",
    codeBlockStyle: "fenced",
    bulletListMarker: "-",
  });
  td.addRule("lineBreak", {
    filter: "br",
    replacement: () => "\n",
  });

  // Use DOMParser to extract only the body content, dropping <head>/<style>/<script>.
  const doc = new DOMParser().parseFromString(html, "text/html");
  const bodyHtml = doc.body.innerHTML;
  return td.turndown(bodyHtml || html);
};

/** Download an HTML artifact's text content as a .md file. */
export const exportHtmlAsMarkdown = (
  htmlContent: string,
  filename: string,
): void => {
  const markdown = htmlToMarkdown(htmlContent);
  downloadAsFile(markdown, `${filename}.md`, "text/markdown");
};

/**
 * Convert an HTML artifact to .docx by first converting to Markdown with
 * Turndown, then passing through the existing exportToDocx pipeline.
 */
export const exportHtmlAsDocx = async (
  htmlContent: string,
  filename: string,
): Promise<void> => {
  const markdown = htmlToMarkdown(htmlContent);
  await exportToDocx(markdown, filename);
};

/**
 * Write content to the clipboard as both text/html and text/plain using the
 * Async Clipboard API. Falls back to plain-text writeText if ClipboardItem is
 * unavailable (older browsers / non-HTTPS).
 *
 * @param htmlContent  - Raw HTML string (used for text/html MIME type).
 * @param plainContent - Plain text or Markdown fallback (used for text/plain).
 */
export const copyAsRichText = async (
  htmlContent: string,
  plainContent: string,
): Promise<void> => {
  try {
    if (typeof ClipboardItem !== "undefined") {
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([plainContent], { type: "text/plain" });
      await navigator.clipboard.write([
        new ClipboardItem({ "text/html": htmlBlob, "text/plain": textBlob }),
      ]);
    } else {
      // Fallback for environments without ClipboardItem support.
      await navigator.clipboard.writeText(plainContent);
    }
  } catch {
    // Last-resort fallback.
    await navigator.clipboard.writeText(plainContent);
  }
};

/**
 * Convert a Markdown string to HTML using remark + remark-html,
 * for use in rich clipboard payloads.
 */
export const markdownToHtml = async (markdown: string): Promise<string> => {
  const file = await remark().use(remarkGfm).use(remarkHtml).process(markdown);
  return String(file);
};

/**
 * Convert a Markdown artifact to PDF via the browser print dialog.
 * Wraps the remark-generated HTML in a minimal full document with
 * readable typography so the output looks like a clean document.
 */
export const exportMarkdownAsPdf = async (
  markdown: string,
  _filename: string,
): Promise<void> => {
  const bodyHtml = await markdownToHtml(markdown);
  const fullHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<style>
  body {
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 15px;
    line-height: 1.7;
    color: #111;
    max-width: 720px;
    margin: 0 auto;
  }
  h1, h2, h3, h4, h5, h6 { margin-top: 1.5em; margin-bottom: 0.4em; line-height: 1.3; }
  p { margin: 0.8em 0; }
  pre { background: #f4f4f4; border-radius: 6px; padding: 1em; overflow: auto; font-size: 13px; }
  code { background: #f4f4f4; border-radius: 3px; padding: 0.15em 0.4em; font-size: 13px; }
  pre code { background: none; padding: 0; }
  blockquote { border-left: 4px solid #ddd; margin: 1em 0; padding: 0.2em 1em; color: #555; }
  table { border-collapse: collapse; width: 100%; margin: 1em 0; }
  th, td { border: 1px solid #ddd; padding: 8px 12px; text-align: left; }
  th { background: #f4f4f4; font-weight: 600; }
  img { max-width: 100%; height: auto; }
  hr { border: none; border-top: 1px solid #ddd; margin: 1.5em 0; }
  a { color: #0066cc; }
</style>
</head>
<body>${bodyHtml}</body>
</html>`;
  exportHtmlAsPdf(fullHtml, _filename);
};
